import { useEffect } from "react";
import { useProject } from "./ProjectContext";

/**
 * Confirmation modal for project switching.
 *
 * PRD-004 DoD: lists destination + consequences (cancels queued/running
 * export jobs per PRD-001's 1-job-per-user rule, clears in-flight UI
 * state). Has explicit Switch + Abort actions. Aborting changes nothing.
 *
 * Visual: matches the prototype's modal pattern
 * (`requirements/ui-patterns.md`).
 */
export function ProjectSwitchModal() {
    const { pendingSwitch, authorizedProjects, confirmSwitch, abortSwitch } = useProject();

    useEffect(() => {
        if (!pendingSwitch) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") abortSwitch();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [pendingSwitch, abortSwitch]);

    if (!pendingSwitch) return null;
    const targetName =
        authorizedProjects.find((p) => p.id === pendingSwitch.to)?.name ?? "Unknown";

    return (
        <div className="modal-overlay" data-testid="project-switch-modal">
            <div className="modal" role="dialog" aria-modal="true" aria-labelledby="switch-title">
                <div className="modal-hdr">
                    <div>
                        <div className="modal-step">
                            <i className="fa-solid fa-folder-tree" aria-hidden="true" /> Switch project
                        </div>
                        <div className="modal-title" id="switch-title">
                            Switch to {targetName}?
                        </div>
                    </div>
                    <button
                        type="button"
                        className="modal-x"
                        onClick={abortSwitch}
                        aria-label="Close"
                    >
                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                </div>
                <div className="modal-body">
                    <div className="callout callout-warn">
                        <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                        <div>
                            <strong>Switching projects will:</strong>
                            <ul style={{ marginTop: 6 }}>
                                <li>
                                    Cancel any queued or running export job (per-user concurrency limit).
                                </li>
                                <li>Discard any in-flight selections and form state.</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className="modal-ftr">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={abortSwitch}
                        data-testid="switch-abort"
                    >
                        Abort
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={confirmSwitch}
                        data-testid="switch-confirm"
                    >
                        Switch project
                    </button>
                </div>
            </div>
        </div>
    );
}
